/**
 * Live geometry of the app chrome — the suite nav bar plus the SiteHeader under it.
 *
 * The bottom edge of the chrome is not a constant: it is 3.5rem with the suite bar
 * collapsed and `calc(56px + 3.5rem)` with it expanded. dashboardLayout publishes both
 * values as CSS custom properties on the chrome row, and everything that has to clear the
 * chrome reads them from here instead of hardcoding an assumption about the bar.
 *
 * The town-square twin is `town-square/src/lib/overlayLayers.ts`; the variable NAMES are
 * deliberately identical in both hosts so a surface that ever moves between them keeps
 * working. This copy carries only the chrome geometry — client has no overlay-portal layer.
 *
 * Always spelled with a fallback. Chrome-free routes (art, admin, onboarding, settings)
 * mount no NavProvider and therefore publish nothing, so an unset variable has to degrade
 * to the pre-suite-bar offset rather than collapse to 0.
 */

/** The SiteHeader's own height — the `3.5rem` its inner row is pinned to. */
export const SITE_HEADER_HEIGHT = '3.5rem';

/**
 * The suite nav bar's expanded height, per `.ahsuite-bar` in @artstorefronts/arthelper-nav's
 * stylesheet.
 *
 * PX, NOT REM, AND NOT INTERCHANGEABLE WITH `SITE_HEADER_HEIGHT`. The package pins the bar
 * at a spec-fixed `height: 56px` (border-box, border included), so this has to be 56px too:
 * `3.5rem` only equals it at a 16px root font-size, and a viewer with a larger browser font
 * would get a gap the bar does not occupy, or an overlap. `SITE_HEADER_HEIGHT` stays rem
 * because the header genuinely is rem-based, which is why the expanded offset below is a
 * `calc()` mixing the two units rather than one unit throughout.
 */
export const SUITE_NAV_HEIGHT = '56px';

/** Live height of the suite nav bar — 56px expanded, 0 collapsed, unset with no chrome. */
export const SUITE_NAV_HEIGHT_VAR = '--ah-suite-nav-height';

/** y coordinate of the SiteHeader's bottom edge: the bar's height plus the header's own. */
export const CHROME_BOTTOM_VAR = '--ah-header-bottom';

/** Where the chrome ends — the offset a viewport-pinned surface below the header wants. */
export const CHROME_BOTTOM_CSS = `var(${CHROME_BOTTOM_VAR}, ${SITE_HEADER_HEIGHT})`;
